import {
	Fragment,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { AlertType, Clinic, OurmedicalArticle } from "../models";
import { useHttp } from "../hooks";
import { Box, Modal, Pagination, Stack } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";

import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import { LightTooltip } from "../components/Common";
import { ClinicStateRows } from "../components/OurMedicalArticles";
import { useNavigate } from "react-router-dom";
import { useAlertContext, useClinicContext, useUserContext } from "../contexts";
import {
	ADMIN,
	apiAccessRequest,
	EXECUTIVE_CENTRE_ADMIN,
	MARKETING
} from "../constants";
import { formatCentreName } from "../helpers";
import { useMsal } from "@azure/msal-react";

const pageSize = 20;

export const OurMedicalBlogs = () => {
	const navigate = useNavigate();
	const { instance } = useMsal();
	const { account } = useUserContext();
	const ourmedicalUrl = `${process.env.REACT_APP_CONSULTS_URL}OurMedicalArticles`;
	const getUrl = `${ourmedicalUrl}/All`;
	const [pageNumber, setPageNumber] = useState(1);

	const { data, isLoading, isError, fetchData } = useHttp<OurmedicalArticle[]>(
		getUrl,
		{
			method: "GET",
			autoFetch: true
		}
	);
	const [isRefreshRequired, setIsRefreshRequired] = useState(false);
	const [displayDeleteModal, setDisplayDeleteModal] = useState(false);
	const [expandedArticleIds, setExpandedArticleIds] = useState<number[]>([]);
	const deleteArticle = useRef<OurmedicalArticle | null>(null);
	const { showAlert, clearAlert } = useAlertContext();
	const { clinics } = useClinicContext();
	const { user } = useUserContext();
	const hasEditPermission =
		user?.roles.includes(ADMIN) ||
		user?.roles.includes(MARKETING) ||
		user?.roles.includes(EXECUTIVE_CENTRE_ADMIN) ||
		false;

	// The api returns every article in one response, so page in the browser
	const totalPages = Math.ceil((data?.length || 0) / pageSize);
	const pagedArticles = useMemo(
		() => data?.slice((pageNumber - 1) * pageSize, pageNumber * pageSize) || [],
		[data, pageNumber]
	);

	const retrieveArticles = useCallback(async () => {
		if (isRefreshRequired) {
			await fetchData();
			setIsRefreshRequired(false);
		}
	}, [isRefreshRequired, fetchData]);

	useEffect(() => {
		retrieveArticles();
	}, [retrieveArticles]);

	useEffect(() => {
		isError
			? showAlert({
					message: "Error retrieving articles",
					alertType: AlertType.error
				})
			: clearAlert();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isError]);

	useEffect(() => {
		!hasEditPermission
			? showAlert({
					message: "You do not have permission to update articles",
					alertType: AlertType.info,
					timeout: true
				})
			: clearAlert();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hasEditPermission]);

	const handleChangePage = (
		event: React.ChangeEvent<unknown>,
		page: number
	) => {
		setExpandedArticleIds([]);
		setPageNumber(page);
	};
	const handleEditClick = useCallback(
		(article: OurmedicalArticle) => {
			if (!hasEditPermission) {
				return;
			}
			navigate("/ourmedicalBlogEdit", { state: { article } });
		},
		[hasEditPermission, navigate]
	);

	const handleDeleteClick = useCallback(
		(article: OurmedicalArticle) => {
			if (!hasEditPermission) {
				return;
			}
			deleteArticle.current = article;
			setDisplayDeleteModal(true);
		},
		[hasEditPermission]
	);

	const handleDeleteResponse = useCallback(
		async (response: boolean) => {
			if (response) {
				try {
					const tokenResponse = await instance.acquireTokenSilent({
						account,
						scopes: apiAccessRequest.scopes
					});
					const headers = {
						...(tokenResponse && {
							Authorization: `Bearer ${tokenResponse.accessToken}`
						})
					};
					const response = await fetch(
						`${ourmedicalUrl}/${deleteArticle.current?.id}`,
						{
							headers,
							method: "DELETE"
						}
					);

					if (response.ok) {
						setIsRefreshRequired(true);
						showAlert({
							message: `${deleteArticle.current?.title} deleted`,
							alertType: AlertType.success
						});
					} else {
						throw new Error("Delete article failed");
					}
				} catch (e) {
					console.info("Delete article failed", e);
				}
			}
			setDisplayDeleteModal(false);
			deleteArticle.current = null;
		},
		[account, instance, ourmedicalUrl, showAlert]
	);

	// Show "All" when the article covers every clinic, otherwise list the names
	// Articles only carry clinic id and name, so match them to the clinics in
	// context to get their state
	const getArticleClinics = (article: OurmedicalArticle): Clinic[] =>
		(article.clinics || []).map(
			(articleClinic) =>
				clinics?.find((clinic) => clinic.id === articleClinic.id) ??
				({
					id: articleClinic.id,
					name: formatCentreName(articleClinic.name)
				} as Clinic)
		);

	const renderClinicsSummary = (article: OurmedicalArticle) => {
		const articleClinics = article.clinics || [];
		if (articleClinics.length === 0) {
			return "";
		}

		const isAllClinics =
			!!clinics?.length &&
			clinics.every((clinic) =>
				articleClinics.some((articleClinic) => articleClinic.id === clinic.id)
			);
		if (isAllClinics) {
			return "All clinics";
		}

		return `${articleClinics.length} clinic${articleClinics.length === 1 ? "" : "s"}`;
	};

	const renderClinicsCell = (article: OurmedicalArticle) => {
		const summary = renderClinicsSummary(article);
		if (!article.clinics?.length) {
			return summary;
		}

		const isExpanded = expandedArticleIds.includes(article.id);
		const toggleExpanded = () => {
			if (isExpanded) {
				setExpandedArticleIds(
					expandedArticleIds.filter((id) => id !== article.id)
				);
			} else {
				const newArticleIds = [...expandedArticleIds];
				newArticleIds.push(article.id);
				setExpandedArticleIds(newArticleIds);
			}
		};

		return (
			<div
				role="button"
				tabIndex={0}
				aria-expanded={isExpanded}
				style={{
					display: "flex",
					alignItems: "center",
					gap: 4,
					cursor: "pointer",
					whiteSpace: "nowrap"
				}}
				onClick={toggleExpanded}
				onKeyDown={(e) => {
					if (e.key === "Enter" || e.key === " ") {
						e.preventDefault();
						toggleExpanded();
					}
				}}
			>
				{isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
				{summary}
			</div>
		);
	};

	const renderTableRow = (article: OurmedicalArticle) => {
		return (
			<Fragment key={article.id}>
				<tr>
					<td>{article.title}</td>
					<td align="left" style={{ display: "flex" }}>
						<img
							src={article.imageUrl}
							alt={article.title}
							style={{ maxWidth: 180 }}
						/>
					</td>
					<td align="left">
						<div style={{ display: "flex", gap: 10 }}>
							<a
								href={article.url}
								target="_blank"
								rel="noreferrer"
								style={{ color: "#1565c0" }}
							>
								<OpenInNewIcon />
							</a>
						</div>
					</td>
					<td align="left">{renderClinicsCell(article)}</td>
					<td align="center">
						{article.displayInApp && (
							<CheckOutlinedIcon style={{ color: "green" }} />
						)}
						{!article.displayInApp && (
							<CloseOutlinedIcon style={{ color: "red" }} />
						)}
					</td>
					<td align="center">{article.displayOrder}</td>
					<td align="left">{renderTooltips(article)}</td>
				</tr>
				{expandedArticleIds.includes(article.id) && (
					<tr>
						<td colSpan={7}>
							<ClinicStateRows clinics={getArticleClinics(article)} />
						</td>
					</tr>
				)}
			</Fragment>
		);
	};

	const renderTooltips = (article: OurmedicalArticle) => {
		return (
			<>
				{hasEditPermission ? (
					<LightTooltip title="Delete">
						<DeleteOutlineIcon
							className="icon"
							style={{ marginRight: 10 }}
							onClick={() => {
								handleDeleteClick(article);
							}}
						/>
					</LightTooltip>
				) : (
					<DeleteOutlineIcon
						className="icon-disabled"
						style={{ marginRight: 10 }}
					/>
				)}

				{hasEditPermission ? (
					<LightTooltip title="Edit">
						<EditOutlinedIcon
							className="icon"
							onClick={() => {
								handleEditClick(article);
							}}
						/>
					</LightTooltip>
				) : (
					<EditOutlinedIcon
						className="icon-disabled"
						onClick={() => {
							handleEditClick(article);
						}}
					/>
				)}
			</>
		);
	};

	const renderDeleteConfirmationModal = () => {
		return (
			<Modal
				open={displayDeleteModal}
				onClose={() => handleDeleteResponse(false)}
				aria-labelledby="parent-modal-title"
				aria-describedby="parent-modal-description"
			>
				<Box className="modal" sx={{ width: 150 }}>
					<h2 id="parent-modal-title">
						Delete '{deleteArticle.current?.title || ""}'
					</h2>
					<p id="parent-modal-description" className="pb-4">
						Are you sure you want to delete article?
					</p>
					<div style={{ display: "flex", justifyContent: "center", gap: 20 }}>
						<button
							className="ch-button ch-button-primary"
							onClick={() => handleDeleteResponse(false)}
						>
							No
						</button>
						<button
							className="ch-button ch-button-secondary"
							onClick={() => handleDeleteResponse(true)}
						>
							Yes
						</button>
					</div>
				</Box>
			</Modal>
		);
	};

	if (isLoading) {
		return (
			<Box className="icon-loading">
				<CircularProgress size={70} />
			</Box>
		);
	} else if (isError) {
		return <></>;
	} else {
		return (
			<>
				<div className="ch-l-text my-4">
					Our Medical Blogs
					{hasEditPermission ? (
						<LightTooltip title="Add">
							<AddCircleOutlineOutlinedIcon
								className="icon"
								style={{
									marginLeft: 20,
									fontSize: 32,
									verticalAlign: "text-top"
								}}
								onClick={() => {
									handleEditClick({ id: 0 } as OurmedicalArticle);
								}}
							/>
						</LightTooltip>
					) : (
						<AddCircleOutlineOutlinedIcon
							className="icon-disabled"
							style={{
								marginLeft: 20,
								fontSize: "1.1em",
								verticalAlign: "text-top"
							}}
						/>
					)}
				</div>
				<table
					aria-label="our medical blogs"
					className="ch-table"
					style={{ minWidth: 650 }}
				>
					<thead>
						<tr>
							<th>Title</th>
							<th>Image</th>
							<th>Article Url</th>
							<th>Clinics</th>
							<th>Display in App</th>
							<th>Display order</th>
							<th />
						</tr>
					</thead>
					{data && (
						<tbody>
							{pagedArticles.map((article) => renderTableRow(article))}
						</tbody>
					)}
				</table>
				<Stack alignItems="center" style={{ marginTop: 10 }}>
					<Pagination
						shape="rounded"
						count={totalPages}
						page={pageNumber}
						onChange={handleChangePage}
					/>
				</Stack>
				{renderDeleteConfirmationModal()}
			</>
		);
	}
};
